import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TipService } from '@interacta-shared/feature-tip';
import { fromEvent, Subject } from 'rxjs';
import { MAX_REC_TIME_LIMIT_MS } from '../models/audio-record.model';
import { WakeLockService } from './wake-lock.service';

@Injectable({ providedIn: 'root' })
export class AudioRecorderService {
    private readonly wakeLockService = inject(WakeLockService);
    private readonly tipService = inject(TipService);

    // record audio
    private mediaStream: MediaStream | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private chunks: BlobPart[] = [];

    private stop$ = new Subject<Blob>();
    private start$ = new Subject<void>();
    private recordingError$ = new Subject<ErrorEvent>();
    // ---

    // audio input visualization
    private audioContext: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private microphone: MediaStreamAudioSourceNode | null = null;
    private volumeDataArray: Uint8Array<ArrayBuffer> | null = null;
    private monitorIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly MONITOR_INTERVAL_MS = 100;
    private readonly WAVE_MAX_HEIGHT_PX = 36;
    // ---

    private recordingIntervalId: ReturnType<typeof setInterval> | null = null;
    private recordingTimeoutId: ReturnType<typeof setTimeout> | null = null;

    readonly durationLimitMs = signal(MAX_REC_TIME_LIMIT_MS);
    readonly recordingTime = signal<number>(0);
    readonly isRecording = signal<boolean>(false);
    // From rec() until MediaRecorder fires `start`: `isRecording` is still
    // false while the microphone is acquired and the start event is pending.
    readonly isStarting = signal<boolean>(false);
    readonly isPaused = signal<boolean>(false);

    /**
     * The following filetypes are in order of priority.
     */
    private readonly mimeTypes = [
        'audio/mp4; codecs="mp4a.40.2"',
        'audio/ogg',
        'audio/webm',
    ];

    private readonly addVolumeBar$ = new Subject<number | null>();

    readonly volumeBarStream = this.addVolumeBar$.asObservable();

    getStart() {
        return this.start$.asObservable();
    }

    getStop() {
        return this.stop$.asObservable();
    }

    getRecordingError() {
        return this.recordingError$.asObservable();
    }

    constructor() {
        fromEvent(window, 'beforeunload')
            .pipe(takeUntilDestroyed())
            .subscribe((event) => {
                if (this.isRecording()) {
                    event.preventDefault();

                    // Included for legacy support, e.g. Chrome/Edge < 119
                    event.returnValue = true;
                }
            });
    }

    async rec(durationLimitMs?: number): Promise<void> {
        if (this.isStarting()) {
            console.warn("Can't start recording: initializing mediaRecorder");
            return;
        }

        this.isStarting.set(true);

        try {
            if (durationLimitMs) this.durationLimitMs.set(durationLimitMs);

            if (!this.mediaRecorder) {
                await this.initRecorder();
            }

            if (
                this.mediaRecorder &&
                this.mediaRecorder.state !== 'recording'
            ) {
                this.startVolumeAnalyser();
                this.mediaRecorder.start();
                this.startRecordingTimer();
                this.start$.next();
            } else {
                console.warn(
                    'mediaRecorder is already recording or unaccessible',
                );
                this.isStarting.set(false);
            }
        } catch (error) {
            console.error('Error starting recording', error);
            this.isStarting.set(false);
        }
    }

    async stop(): Promise<void> {
        this.mediaRecorder?.stop();
    }

    async pause(): Promise<void> {
        this.mediaRecorder?.pause();
    }

    async resume(): Promise<void> {
        this.mediaRecorder?.resume();
    }

    private async initRecorder(): Promise<void> {
        try {
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
        } catch (e) {
            if (e instanceof DOMException) {
                if (e.name === 'NotAllowedError') {
                    this.tipService.warn(
                        'SHARED.ERROR.GET_AUDIO_MEDIA_DEVICE_NOT_ALLOWED_ERROR',
                    );
                } else {
                    this.tipService.error(e.message);
                }
            } else {
                throw e;
            }
        }
        if (this.mediaStream) {
            this.initMediaRecorder(this.mediaStream, {
                mimeType: this.getFirstSupportedMimeType(),
            });
            this.initVolumeAnalyser(this.mediaStream);
        }
    }

    private finalize() {
        this.stopVolumeAnalyser();
        this.clearTimers();
        this.mediaStream?.getTracks().forEach((t) => t.stop());
        this.chunks = [];
        this.isRecording.set(false);
        this.isPaused.set(false);
        this.wakeLockService.release();
        this.mediaRecorder = null;
        this.isStarting.set(false);
    }

    private initMediaRecorder(
        stream: MediaStream,
        option: MediaRecorderOptions,
    ): void {
        this.mediaRecorder = new MediaRecorder(stream, option);

        this.mediaRecorder.onstart = () => {
            this.isRecording.set(true);
            this.isStarting.set(false);
            this.wakeLockService.request();
        };
        this.mediaRecorder.onpause = () => {
            this.isPaused.set(true);
        };

        this.mediaRecorder.onresume = () => {
            this.isPaused.set(false);
        };

        this.mediaRecorder.ondataavailable = (ev: BlobEvent) => {
            this.chunks.push(ev.data);
        };

        this.mediaRecorder.onerror = (err) => {
            console.error('MediaRecorder error', err);
            this.finalize();
            this.recordingError$.next(err);
        };

        this.mediaRecorder.onstop = () => {
            const blob = new Blob(this.chunks, {
                type: option.mimeType,
            });

            this.stop$.next(blob);

            this.finalize();
        };
    }

    private getFirstSupportedMimeType(): string {
        for (const ft of this.mimeTypes) {
            if (MediaRecorder.isTypeSupported(ft)) return ft;
        }
        throw new Error('No mediatype supported for recording');
    }

    private startVolumeAnalyser(): void {
        this.monitorIntervalId = setInterval(
            () => !this.isPaused() && this.addVolumeBar$.next(this.getVolume()),
            this.MONITOR_INTERVAL_MS,
        );
    }

    private stopVolumeAnalyser(): void {
        if (this.monitorIntervalId) {
            clearInterval(this.monitorIntervalId);
            this.monitorIntervalId = null;
        }
        this.microphone?.disconnect();
        this.audioContext?.close();
    }

    /**
     * Calculates and returns a normalized volume level based on the maximum instantaneous peak volume.
     * It processes audio data from an analyzer node, computes the maximum deviation from the center of the waveform,
     * and normalizes this value to a predefined maximum height.
     *
     * one of the future developments could be to calculate the RMS (Root Mean Square) level,
     * which is a more accurate measure of the perceived volume or energy of the signal.
     */
    private getVolume(): number | null {
        if (!this.analyser || !this.volumeDataArray) return null;

        this.analyser.getByteTimeDomainData(this.volumeDataArray);

        const volume =
            this.volumeDataArray.reduce(
                (max, current) => Math.max(max, Math.abs(current - 127)),
                0,
            ) / 128;

        const volumePercent = Math.max(1, volume * 100);
        const normalizedVolume = Math.max(
            1,
            (volumePercent / 100) * this.WAVE_MAX_HEIGHT_PX,
        );

        return normalizedVolume;
    }

    private startRecordingTimer(): void {
        this.recordingTime.set(0);
        this.recordingIntervalId = setInterval(() => {
            if (!this.isPaused()) {
                this.recordingTime.set(this.recordingTime() + 1);
            }
        }, 1000);

        this.recordingTimeoutId = setTimeout(() => {
            this.stop();
        }, this.durationLimitMs());
    }

    private clearTimers(): void {
        if (this.recordingIntervalId) {
            clearInterval(this.recordingIntervalId);
            this.recordingIntervalId = null;
        }
        if (this.recordingTimeoutId) {
            clearTimeout(this.recordingTimeoutId);
            this.recordingTimeoutId = null;
        }
    }

    private initVolumeAnalyser(stream: MediaStream): void {
        this.audioContext = new AudioContext();

        this.microphone = this.audioContext.createMediaStreamSource(stream);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 128;
        this.volumeDataArray = new Uint8Array(this.analyser.fftSize);

        this.microphone.connect(this.analyser);
    }
}
